/* import __COLOCATED_TEMPLATE__ from './company-create.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { htmlEntitiesDecode } from 'sh-www/utils/misc';
import { action } from '@ember/object';
import { showModal } from 'sh-www/utils/modal-controls';
import {
  handleFormErrorOrModal,
  clearErrors,
} from 'sh-www/utils/handles-errors';


/*
export type CompanyCreateComponentArgs {
  createCompanyTask: Task,
  loadCompanyGroupsTask: Task,
}
*/

const CLASS_NAME = 'SH-c-company-create';

const DEFAULT_NO_GROUP_TEXT = 'No Group';

/** @type {string[]} */
const FORM_INPUTS = [
  'name',
  'website',
  'location_id',
  'description',
  'generic-exception',
];

/**
 * This is the component for creating a new Company from admin on Ember.
 */
class CompanyCreate extends Component {
  @service store;

  @tracked errors = {};

  /**
   * @type {String}     New company description selected by the user.
   */
  @tracked selectedDescription = '';

  /**
   * @type {CompanyGroup|null}     New companyGroup selected by the user.
   */
  @tracked selectedCompanyGroup = null;

  /**
   * @type {Location} Selected Location
   */
  @tracked location = null;

  /**
   * @type {String}     Selected location name
   */
   @tracked locationInputValue = '';

  /**
   * @type {String}     New company name selected by the user.
   */
  @tracked selectedCompanyName = '';

  /** @type {Boolean} */
  @tracked selectedSharesRates = false;

  /**
   * @type {String} New company domain selected by the user.
   */
  @tracked selectedDomainName = null;

  get classNames() {
    return CLASS_NAME;
  }

  /**
   * List of options for the company group select.
   *
   * @type {Ember.A<Object>}
   */
  get companyGroupOptions() {
    const companyGroups = this.args.loadCompanyGroupsTask.lastSuccessful?.value || [];

    const options = [];

    options.push({
      key: DEFAULT_NO_GROUP_TEXT,
      value: null,
    });

    companyGroups.forEach(companyGroup => {
      options.push({
        key: htmlEntitiesDecode(companyGroup.name),
        value: companyGroup.id,
      });
    });

    return options;
  }

  get companyGroupName() {
    return this.selectedCompanyGroup?.name ?? DEFAULT_NO_GROUP_TEXT;
  }

  get formDisabled() {
    return this.args.createCompanyTask.isRunning;
  }

  get recommendSubmit() {
    return !this.formDisabled
      && this.selectedCompanyName
      && this.location;
  }

  /**
   * @param {Location} location
   */
  @action
  handleLocationChange(location) {
    this.location = location;
    this.locationInputValue = location?.formattedAddress ?? '';

    if (this.errors?.location_id) {
      this.errors = { ...this.errors, location_id: undefined };
    }
  }

  /**
   * @param {String} value
   */
  @action
  handleLocationInput(value) {
    this.locationInputValue = value;
  }

  /**
   * @param {Object} companyGroupOption The CompanyGroup option selected
   */
  @action
  onCompanyGroupSelected({ value }) {
    // clear any related error
    if (this.errors?.group) {
      this.errors = { ...this.errors, group: undefined };
    }

    if (value === null) {
      this.selectedCompanyGroup = null;
    } else {
      try {
        this.selectedCompanyGroup = this.store.peekRecord('companyGroup', value);
      } catch (e) {
        // reset group state
        this.selectedCompanyGroup = null;

        // display error
        this.errors = { ...this.errors, group: 'Error selecting company group' };
      }
    }
  }

  /**
   * @param {Boolean} isSharingRates
   */
  @action
  onCompanySharingRatesClicked(isSharingRates) {
    this.selectedSharesRates = isSharingRates;
  }

  /**
   * @param {String} text
   */
  @action
  onUpdateCompanyDescriptionText(text) {
    this.selectedDescription = text;

    if (this.errors?.description) {
      this.errors = { ...this.errors, description: undefined };
    }
  }

  /**
   * @param {String} text
   */
  @action
  onUpdateCompanyNameText(text) {
    this.selectedCompanyName = text;

    if (this.errors?.name) {
      this.errors = { ...this.errors, name: undefined };
    }
  }

  /**
   * @param {String} text
   */
  @action
  onUpdateDomainText(text) {
    this.selectedDomainName = text;

    if (this.errors?.website) {
      this.errors = { ...this.errors, website: undefined };
    }
  }

  @action
  onSubmitNewCompanyClicked() {
    clearErrors(this);

    showModal(
      this,
      'confirmation',
      {
        isShowCancelButton: true,
        titleText: 'Are you sure?',
        bodyText: `This will create a company`,
        actionButtonText: 'Confirm',
        confirmAction: () => {
          this.args.createCompanyTask.perform(
            this.selectedCompanyName,
            this.selectedDescription,
            this.location,
            this.selectedDomainName,
            this.selectedCompanyGroup,
            this.selectedSharesRates
          ).catch((e) => {
            handleFormErrorOrModal(
              this,
              e,
              FORM_INPUTS,
              true,
              'Failed to create company',
            )
          });
        }
      }
    );
  }
}

export default CompanyCreate;
